import { z } from 'zod';
import {
  RESPUESTAS_TERNARIAS,
  RESULTADOS_INSPECCION,
  TIPOS_CAMPO_PROCEDIMIENTO,
  TIPOS_ITEM_PLANTILLA,
  TIPOS_TRABAJO,
  type ResultadoInspeccion,
  type TipoCampoProcedimiento,
  type TipoItemPlantilla,
} from './enums.js';
import { paginacionSchema, uuid } from './contratos.js';

/**
 * Motor de procedimientos — §8.
 *
 * Aquí viven las tres formas del mismo árbol, que conviene no confundir:
 *
 *  · `ItemPlantilla` — el borrador de trabajo, editable, con los procedimientos
 *    anidados aún como referencia.
 *  · `ItemCongelado` — el árbol renderizado al publicar y copiado al adjuntar,
 *    con los anidados ya resueltos en línea. Es inmutable (§2.3).
 *  · `ItemVisible` — lo que el ejecutor ve en un momento dado, tras evaluar las
 *    condiciones contra las respuestas registradas hasta ese punto.
 */

// ─── Límites (§8.7) ──────────────────────────────────────────────────────────

/** Contador visible en el editor de la plataforma actual. */
export const MAX_CAMPOS_PLANTILLA = 500;
/** Profundidad máxima de anidamiento, como corte de seguridad al renderizar. */
export const MAX_PROFUNDIDAD_ANIDADA = 5;

// ─── Autoría del árbol ───────────────────────────────────────────────────────

/**
 * Operadores de condición (§8.5): `UNO_DE`/`NINGUNO_DE` comparan la respuesta
 * del padre contra una lista de valores elegidos; `NO_SELECCIONADO` no necesita
 * valores — se cumple cuando el padre todavía no tiene respuesta.
 */
export const OPERADORES_CONDICION = [
  'UNO_DE',
  'NINGUNO_DE',
  'NO_SELECCIONADO',
  'MAYOR_QUE',
  'MENOR_QUE',
  'IGUAL_A',
  'DISTINTO_A',
  'ENTRE',
] as const;
export type OperadorCondicion = (typeof OPERADORES_CONDICION)[number];

/** Operadores que aplican a cualquier campo con respuesta. */
const OPERADORES_GENERALES: readonly OperadorCondicion[] = ['UNO_DE', 'NINGUNO_DE', 'NO_SELECCIONADO'];

/**
 * Comparaciones sobre un número (umbral: supera, inferior a, igual a, distinto a)
 * y el rango inclusivo «entre» (dos números). Solo para campos numéricos.
 */
const OPERADORES_NUMERICOS: readonly OperadorCondicion[] = [
  'MAYOR_QUE',
  'MENOR_QUE',
  'IGUAL_A',
  'DISTINTO_A',
  'ENTRE',
];

export function esOperadorNumerico(operador: OperadorCondicion): boolean {
  return OPERADORES_NUMERICOS.includes(operador);
}

/** Cuántos números necesita cada operador numérico: «entre» usa dos, el resto uno. */
function cantidadNumeros(operador: OperadorCondicion): number {
  return operador === 'ENTRE' ? 2 : 1;
}

function esNumeroValido(texto: string): boolean {
  return texto.trim() !== '' && Number.isFinite(Number(texto.replace(',', '.')));
}

export const condicionSchema = z
  .object({
    operador: z.enum(OPERADORES_CONDICION),
    valores: z.array(z.string().min(1)).default([]),
  })
  .superRefine((c, ctx) => {
    if (c.operador !== 'NO_SELECCIONADO' && c.valores.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['valores'], message: 'Elija al menos un valor' });
    }
    if (esOperadorNumerico(c.operador)) {
      const ok = c.valores.length === cantidadNumeros(c.operador) && c.valores.every(esNumeroValido);
      if (!ok) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['valores'],
          message: c.operador === 'ENTRE' ? 'Indique dos números' : 'Indique un único número',
        });
      }
    }
  });
export type Condicion = z.infer<typeof condicionSchema>;

export const itemPlantillaSchema = z
  .object({
    id: uuid.optional(),
    tipoItem: z.enum(TIPOS_ITEM_PLANTILLA),
    nombre: z.string().trim().min(1, 'Requerido').max(500),
    /**
     * §8.4: las instrucciones son sustantivas, no decorativas. Definen el
     * criterio que distingue APROBADO de ALERTA y de FALLA, y por eso admiten
     * el largo que haga falta.
     */
    instrucciones: z.string().trim().max(4000).optional(),
    tipoCampo: z.enum(TIPOS_CAMPO_PROCEDIMIENTO).optional(),
    opciones: z.array(z.string().trim().min(1).max(300)).max(200).default([]),
    requerido: z.boolean().default(false),
    permiteAdjuntos: z.boolean().default(false),
    permiteNotas: z.boolean().default(false),
    /**
     * §8.4/§8.9: evidencia fotográfica obligatoria según la importancia del
     * ítem — a diferencia de `permiteAdjuntos` (el técnico puede adjuntar si
     * quiere), esto bloquea el cierre de la ejecución si falta al menos una
     * foto en la respuesta.
     */
    evidenciaObligatoria: z.boolean().default(false),
    /** Ids de adjuntos ya subidos (huérfanos) a vincular como imágenes de referencia del ítem. */
    imagenesReferencia: z.array(uuid).max(10).default([]),
    puntaje: z.number().min(0).max(1000).optional(),
    condicion: condicionSchema.optional(),
    plantillaAnidadaId: uuid.optional(),
    medidorId: uuid.optional(),
    hijos: z.array(z.lazy((): z.ZodTypeAny => itemPlantillaSchema)).default([]),
  })
  .superRefine((item, ctx) => {
    // Dos opciones iguales (sin contar espacios ni mayúsculas) son la misma
    // respuesta: se rechazan, para que no aparezcan dos veces en las condiciones.
    if (item.tipoItem === 'CAMPO' && (item.tipoCampo === 'ELECCION_MULTIPLE' || item.tipoCampo === 'LISTA_COMPROBACION')) {
      const vistas = new Set<string>();
      for (const opcion of item.opciones) {
        const clave = opcion.trim().toLowerCase();
        if (vistas.has(clave)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['opciones'], message: `La opción «${opcion.trim()}» está repetida.` });
          break;
        }
        vistas.add(clave);
      }
    }
    if (item.tipoItem === 'CAMPO' && !item.tipoCampo) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tipoCampo'], message: 'Elija el tipo de campo' });
    }
    if (item.tipoItem === 'PROCEDIMIENTO_ANIDADO' && !item.plantillaAnidadaId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plantillaAnidadaId'],
        message: 'Elija el procedimiento a insertar',
      });
    }
    if (item.tipoCampo === 'LECTURA_MEDIDOR' && !item.medidorId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['medidorId'], message: 'Elija el medidor a leer' });
    }
    if (
      item.tipoCampo &&
      ['ELECCION_MULTIPLE', 'LISTA_COMPROBACION'].includes(item.tipoCampo) &&
      item.opciones.length === 0
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['opciones'], message: 'Agregue al menos una opción' });
    }
  });

export interface ItemPlantilla {
  id?: string;
  tipoItem: TipoItemPlantilla;
  nombre: string;
  instrucciones?: string;
  tipoCampo?: TipoCampoProcedimiento;
  opciones: string[];
  requerido: boolean;
  permiteAdjuntos: boolean;
  permiteNotas: boolean;
  evidenciaObligatoria: boolean;
  imagenesReferencia: string[];
  puntaje?: number;
  condicion?: Condicion;
  plantillaAnidadaId?: string;
  medidorId?: string;
  hijos: ItemPlantilla[];
}

/** Imagen de referencia embebida (id + url de lectura + nombre del archivo). */
export interface ImagenReferencia {
  id: string;
  url: string;
  nombreArchivo: string;
}

export const plantillaSchema = z.object({
  nombre: z.string().trim().min(1, 'Requerido').max(300),
  descripcion: z.string().trim().max(4000).optional(),
  categoriaId: uuid.nullish(),
  puntuacionActiva: z.boolean().default(false),
  /** Tipo de trabajo por defecto de las OT que adjunten este procedimiento. */
  tipoTrabajo: z.enum(TIPOS_TRABAJO).nullish(),
  /** Etiquetas de la plantilla (§8.2 "Configuración"): ids de tipos de falla,
   * activos y ubicaciones que ya existen — no crean datos nuevos, solo
   * relacionan. */
  tiposFalla: z.array(uuid).max(50).default([]),
  /** Tipos de activo a los que aplica: al crear una OT de sus activos, se sugiere primero. */
  tiposActivo: z.array(uuid).max(50).default([]),
  activos: z.array(uuid).max(50).default([]),
  ubicaciones: z.array(uuid).max(50).default([]),
  /**
   * Grupos responsables de ejecutar este procedimiento (§8.2 "Configuración").
   * También controla su visibilidad (§ pedido): el acceso es opt-in — un
   * usuario no administrador solo puede elegirlo al crear una OT si
   * pertenece a alguno de estos grupos; sin ninguno asignado, no lo ve.
   */
  grupos: z.array(uuid).max(50).default([]),
});
export type PlantillaEntrada = z.infer<typeof plantillaSchema>;

export const arbolSchema = z.object({
  items: z.array(itemPlantillaSchema).max(MAX_CAMPOS_PLANTILLA * 2),
});

// ─── Árbol congelado (§2.3) ──────────────────────────────────────────────────

/**
 * Copia inmutable que se guarda en la ejecución. Lleva `idOrigen` para poder
 * rastrear de qué ítem de plantilla salió, e `idCongelado` —estable dentro de la
 * ejecución— como clave de las respuestas: los anidados se expanden, de modo que
 * un mismo ítem de origen puede aparecer más de una vez.
 */
export interface ItemCongelado {
  idCongelado: string;
  idOrigen: string;
  tipoItem: TipoItemPlantilla;
  nombre: string;
  instrucciones: string | null;
  tipoCampo: TipoCampoProcedimiento | null;
  opciones: string[];
  requerido: boolean;
  permiteAdjuntos: boolean;
  permiteNotas: boolean;
  evidenciaObligatoria: boolean;
  /** Congeladas al publicar: cambiar la referencia después no altera lo ya publicado. */
  imagenesReferencia: ImagenReferencia[];
  puntaje: number | null;
  condicion: Condicion | null;
  medidorId: string | null;
  /** Plantilla de la que provino, cuando se expandió un procedimiento anidado. */
  plantillaOrigenId: string | null;
  hijos: ItemCongelado[];
}

export interface EstructuraCongelada {
  plantillaId: string;
  plantillaNombre: string;
  version: number;
  puntuacionActiva: boolean;
  congeladoEl: string;
  items: ItemCongelado[];
}

// ─── Respuestas ──────────────────────────────────────────────────────────────

/**
 * Firma dibujada en pantalla con el dedo. `trazo` es el PNG del lienzo en
 * base64 (data URL): se guarda junto a la respuesta, sin subirlo como adjunto.
 */
export const firmaSchema = z.object({
  firmante: z.string().trim().min(1).max(200),
  trazo: z.string().startsWith('data:image/png;base64,').max(400_000),
  fechaHora: z.string(),
});
export type Firma = z.infer<typeof firmaSchema>;

/** Valor tipado según el tipo de campo. El servidor lo valida contra el ítem. */
export const valorRespuestaSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.string()),
  z.null(),
  firmaSchema,
  z.object({ valor: z.number(), medidorId: uuid }),
]);

export const respuestaSchema = z.object({
  idCongelado: z.string().min(1),
  valor: valorRespuestaSchema,
  nota: z.string().trim().max(4000).optional(),
  adjuntos: z.array(uuid).max(20).default([]),
});
export type RespuestaEntrada = z.infer<typeof respuestaSchema>;

export interface Respuesta {
  idCongelado: string;
  valor: unknown;
  nota: string | null;
  adjuntos: string[];
  respondidoPor: string | null;
  respondidoEl: string;
  ordenTrabajoCorrectivaId: string | null;
}

// ─── Estado de una ejecución ─────────────────────────────────────────────────

export interface ResumenResultados {
  aprobado: number;
  alerta: number;
  falla: number;
  /** Campos visibles que aún no tienen respuesta. */
  sinResponder: number;
  /** De los anteriores, los que además son requeridos y bloquean el cierre. */
  requeridosPendientes: number;
  totalVisibles: number;
}

export interface Ejecucion {
  id: string;
  ordenTrabajoId: string;
  plantillaId: string;
  plantillaNombre: string;
  plantillaVersion: number;
  estado: 'EN_PROGRESO' | 'COMPLETADA';
  puntuacionActiva: boolean;
  puntajeTotal: string | null;
  porcentaje: string | null;
  resumen: ResumenResultados;
  estructura: EstructuraCongelada;
  respuestas: Respuesta[];
  iniciadoEl: string;
  completadoEl: string | null;
}

// ─── Puntuación (§8.6) ───────────────────────────────────────────────────────

/**
 * Peso de cada resultado de inspección sobre el puntaje del campo.
 *
 * §8.6 activa la puntuación pero no fija cómo puntúa una inspección, así que la
 * regla se define aquí de forma explícita en lugar de quedar enterrada en el
 * servicio: APROBADO suma completo, ALERTA la mitad —el hallazgo existe pero no
 * inhabilita— y FALLA cero.
 */
export const PESO_INSPECCION: Record<ResultadoInspeccion, number> = {
  APROBADO: 1,
  ALERTA: 0.5,
  FALLA: 0,
};

/**
 * Respuestas que quedan fuera del denominador: «no aplica» no puede penalizar
 * a una sede por algo que no tiene.
 */
export function cuentaParaPuntaje(valor: unknown): boolean {
  return valor !== 'NA' && valor !== null && valor !== undefined;
}

/** Los tres tipos de campo cuyo valor puede disparar una acción correctiva. */
export const TIPOS_CAMPO_EVALUABLES: readonly TipoCampoProcedimiento[] = ['INSPECCION', 'SI_NO_NA'];

export function esFalla(tipoCampo: TipoCampoProcedimiento | null, valor: unknown): boolean {
  if (tipoCampo === 'INSPECCION') return valor === 'FALLA';
  // Un «No» en un campo ternario es un hallazgo, no una falla: §8.9 solo hace
  // que FALLA genere correctiva, y ampliarlo llenaría el backlog de ruido.
  return false;
}

export function esAlerta(tipoCampo: TipoCampoProcedimiento | null, valor: unknown): boolean {
  return tipoCampo === 'INSPECCION' && valor === 'ALERTA';
}

/** Valores admitidos por tipo de campo, para validar en el servidor. */
export const VALORES_CERRADOS: Partial<Record<TipoCampoProcedimiento, readonly string[]>> = {
  INSPECCION: RESULTADOS_INSPECCION,
  SI_NO_NA: RESPUESTAS_TERNARIAS,
};

/**
 * Un check que salió mal exige respaldo fotográfico para poder cerrar el
 * checklist — automático, sin que nadie tenga que marcar nada al armar la
 * plantilla (distinto de `evidenciaObligatoria`, que es un requisito fijo por
 * campo sin importar la respuesta). Vive junto a `esFalla`/`esAlerta` porque
 * cliente y servidor necesitan el mismo criterio: el cliente para mostrar el
 * control de foto en el momento de responder, el servidor para bloquear el
 * cierre si falta.
 *
 * Deliberadamente más estricto que `esFalla`: un ALERTA de inspección no basta
 * (no inhabilita, ver `PESO_INSPECCION`), pero un «No» en SI_NO_NA sí — a
 * diferencia de la correctiva (que ese caso no dispara, ver `esFalla`), acá no
 * se trata de abrir trabajo nuevo sino de dejar evidencia de lo que se
 * encontró mal.
 */
export function requiereEvidenciaPorRespuesta(tipoCampo: TipoCampoProcedimiento | null, valor: unknown): boolean {
  if (tipoCampo === 'INSPECCION') return valor === 'FALLA';
  if (tipoCampo === 'SI_NO_NA') return valor === 'NO';
  return false;
}

// ─── Evaluación del árbol (§8.5) ─────────────────────────────────────────────

/**
 * Vive aquí, y no en el servidor, porque los dos lados la necesitan y deben
 * coincidir: el cliente para decidir qué campos pinta mientras el técnico
 * responde, y el servidor para decidir qué campos exige al cerrar. Si cada uno
 * evaluara las condiciones a su manera, el checklist mostraría una cosa y el
 * cierre reclamaría otra.
 */

/** Respuestas indexadas por el identificador congelado del campo. */
export type MapaRespuestas = Map<string, { valor: unknown }>;

/** Respuestas que se comparan como número, no como texto. */
const TIPOS_CONDICION_NUMERICA: readonly TipoCampoProcedimiento[] = ['NUMERICO', 'IMPORTE', 'LECTURA_MEDIDOR'];

/**
 * Campos cuya respuesta es una lista de ids o un objeto (adjuntos): no hay un
 * valor que el autor pueda escribir, así que solo admiten presencia/ausencia.
 */
const TIPOS_CONDICION_SOLO_PRESENCIA: readonly TipoCampoProcedimiento[] = ['IMAGEN_ARCHIVO'];

/** Operadores que el editor puede ofrecer para un campo disparador. */
export function operadoresCondicionPara(tipoCampo: TipoCampoProcedimiento | null): readonly OperadorCondicion[] {
  if (tipoCampo && TIPOS_CONDICION_SOLO_PRESENCIA.includes(tipoCampo)) return ['NO_SELECCIONADO'];
  if (tipoCampo && TIPOS_CONDICION_NUMERICA.includes(tipoCampo)) return [...OPERADORES_NUMERICOS, 'NO_SELECCIONADO'];
  return OPERADORES_GENERALES;
}

/**
 * Valores discretos que un campo puede ofrecer como condición. `null` cuando el
 * tipo no tiene un conjunto cerrado (texto, número, fecha…): ahí el autor los escribe.
 */
export function valoresDiscretosCondicion(tipoCampo: TipoCampoProcedimiento, opciones: string[]): string[] | null {
  switch (tipoCampo) {
    case 'CASILLA':
      return ['true', 'false'];
    case 'INSPECCION':
      return [...RESULTADOS_INSPECCION];
    case 'SI_NO_NA':
      return [...RESPUESTAS_TERNARIAS];
    case 'ELECCION_MULTIPLE':
    case 'LISTA_COMPROBACION': {
      // Sin espacios finales y sin repetidas: «Sí» y «Sí » son la misma opción.
      const limpias = [...new Set(opciones.map((o) => o.trim()).filter(Boolean))];
      return limpias.length > 0 ? limpias : null;
    }
    default:
      return null;
  }
}

/** Una respuesta vacía no cuenta como contestada (texto en blanco, nada marcado). */
export function estaVacia(valor: unknown): boolean {
  if (valor === null || valor === undefined) return true;
  if (typeof valor === 'string') return valor.trim() === '';
  if (Array.isArray(valor)) return valor.length === 0;
  return false;
}

/** ¿El campo tiene una respuesta registrada y no vacía? Lo usan la obligatoriedad y las condiciones. */
export function tieneRespuesta(respuestas: MapaRespuestas, idCongelado: string): boolean {
  const respuesta = respuestas.get(idCongelado);
  return respuesta !== undefined && !estaVacia(respuesta.valor);
}

/**
 * Normaliza lo que el autor escribe como valor de condición para un campo sin
 * opciones fijas. `null` si no es válido para ese tipo.
 */
export function normalizarValorCondicion(tipoCampo: TipoCampoProcedimiento | null, valor: string): string | null {
  const limpio = valor.trim();
  if (limpio === '') return null;
  if (tipoCampo && TIPOS_CONDICION_NUMERICA.includes(tipoCampo)) {
    const numero = Number(limpio.replace(',', '.'));
    return Number.isFinite(numero) ? String(numero) : null;
  }
  if (tipoCampo === 'FECHA') {
    // AAAA-MM-DD, que es como se guarda la respuesta. Se rechazan fechas
    // inexistentes (2026-02-30) que `Date` acepta sin avisar.
    const fecha = new Date(`${limpio}T00:00:00Z`);
    return !Number.isNaN(fecha.getTime()) && fecha.toISOString().slice(0, 10) === limpio ? limpio : null;
  }
  return limpio;
}

/**
 * Valor numérico de una respuesta, o `NaN` si no lo es. LECTURA_MEDIDOR puede
 * llegar envuelta como { valor, medidorId }.
 */
function numeroDeRespuesta(respuesta: unknown): number {
  const crudo =
    typeof respuesta === 'object' && respuesta !== null && 'valor' in respuesta
      ? (respuesta as { valor: unknown }).valor
      : respuesta;
  if (typeof crudo !== 'number' && typeof crudo !== 'string') return Number.NaN;
  return Number(crudo);
}

/** Evalúa un operador numérico contra la respuesta. `ENTRE` es inclusivo en ambos extremos. */
function cumpleUmbral(operador: OperadorCondicion, actual: number, umbrales: number[]): boolean {
  const primero = umbrales[0] ?? Number.NaN;
  const segundo = umbrales[1] ?? Number.NaN;
  switch (operador) {
    case 'MAYOR_QUE':
      return actual > primero;
    case 'MENOR_QUE':
      return actual < primero;
    case 'IGUAL_A':
      return actual === primero;
    case 'DISTINTO_A':
      return actual !== primero;
    case 'ENTRE':
      return actual >= primero && actual <= segundo;
    default:
      return false;
  }
}

/** Compara una respuesta con un valor de condición, según el tipo del campo que responde. */
function coincideValor(tipoCampo: TipoCampoProcedimiento | null, respuesta: unknown, valorCondicion: string): boolean {
  if (tipoCampo && TIPOS_CONDICION_NUMERICA.includes(tipoCampo)) {
    const numero = numeroDeRespuesta(respuesta);
    return Number.isFinite(numero) && numero === Number(valorCondicion);
  }
  return String(respuesta).trim().toLowerCase() === valorCondicion.trim().toLowerCase();
}

/**
 * §8.5: la condición de un ítem se evalúa contra la respuesta de su padre. Sin
 * respuesta del padre, el campo se trata como "no respondido": `NO_SELECCIONADO`
 * y `NINGUNO_DE` se cumplen; `UNO_DE` no. Así una rama condicionada nunca
 * queda bloqueando el cierre por un campo que el padre dejó en blanco.
 */
export function cumpleCondicion(
  item: ItemCongelado,
  padre: ItemCongelado | null,
  respuestas: MapaRespuestas,
): boolean {
  if (!item.condicion) return true;
  const { operador, valores } = item.condicion;

  const respuesta = padre ? respuestas.get(padre.idCongelado) : undefined;
  const sinRespuesta = !respuesta || estaVacia(respuesta.valor);
  if (operador === 'NO_SELECCIONADO') return sinRespuesta;
  if (esOperadorNumerico(operador)) {
    // Sin respuesta numérica no hay umbral que cumplir.
    if (!respuesta || sinRespuesta) return false;
    const actual = numeroDeRespuesta(respuesta.valor);
    if (!Number.isFinite(actual)) return false;
    return cumpleUmbral(operador, actual, valores.map((v) => Number(v)));
  }
  if (!respuesta || sinRespuesta) return operador === 'NINGUNO_DE';

  const tipo = padre?.tipoCampo ?? null;
  const crudos = Array.isArray(respuesta.valor) ? respuesta.valor : [respuesta.valor];
  const algunoCoincide = crudos.some((r) => valores.some((c) => coincideValor(tipo, r, c)));
  return operador === 'NINGUNO_DE' ? !algunoCoincide : algunoCoincide;
}

/**
 * Formas en que el autor puede escribir una respuesta ternaria en un CSV o en el
 * editor («Sí», «N/A»…). La interfaz muestra etiquetas y el servidor guarda
 * códigos (`RESPUESTAS_TERNARIAS`); sin este mapa, «N/A» nunca encontraba su código.
 */
const ALIAS_RESPUESTA_TERNARIA: Readonly<Record<string, string>> = {
  si: 'SI',
  sí: 'SI',
  no: 'NO',
  na: 'NA',
  'n/a': 'NA',
};

/**
 * Canoniza un valor de condición contra las opciones discretas del disparador
 * (sin distinguir mayúsculas, espacios ni etiquetas). `undefined` si no existe.
 */
export function canonicoDiscreto(discretos: readonly string[], crudo: string): string | undefined {
  const clave = crudo.trim().toLowerCase();
  const directo = discretos.find((d) => d.trim().toLowerCase() === clave);
  if (directo !== undefined) return directo;
  const alias = ALIAS_RESPUESTA_TERNARIA[clave];
  return alias !== undefined ? discretos.find((d) => d === alias) : undefined;
}

export type ResolucionCondicion = { condicion: Condicion } | { error: string };

/**
 * Valida y normaliza una condición contra el campo que la dispara. El guardado
 * de la plantilla y el editor usan esta misma función, para aceptar exactamente
 * lo mismo. Los valores discretos se canonizan (p. ej. "si" → "SI") y los libres
 * se normalizan según su tipo (p. ej. "4,50" → "4.5").
 */
export function resolverCondicion(
  condicion: Condicion,
  padre: { tipoCampo: TipoCampoProcedimiento | null; opciones: string[] },
): ResolucionCondicion {
  const tipo = padre.tipoCampo;
  if (!operadoresCondicionPara(tipo).includes(condicion.operador)) {
    return { error: 'Este operador no aplica a este tipo de campo.' };
  }
  if (condicion.operador === 'NO_SELECCIONADO') return { condicion: { operador: 'NO_SELECCIONADO', valores: [] } };

  if (esOperadorNumerico(condicion.operador)) {
    const cantidad = cantidadNumeros(condicion.operador);
    if (condicion.valores.length !== cantidad || !condicion.valores.every(esNumeroValido)) {
      return { error: cantidad === 2 ? 'Indique dos números para «entre».' : 'Indique un único número como umbral.' };
    }
    const numeros = condicion.valores.map((v) => Number(v.trim().replace(',', '.')));
    if (cantidad === 2 && (numeros[0] ?? 0) > (numeros[1] ?? 0)) {
      return { error: 'En «entre», el primer número no puede ser mayor que el segundo.' };
    }
    return { condicion: { operador: condicion.operador, valores: numeros.map(String) } };
  }

  const discretos = tipo ? valoresDiscretosCondicion(tipo, padre.opciones) : null;
  const valores: string[] = [];
  for (const crudo of condicion.valores) {
    if (discretos) {
      const canonico = canonicoDiscreto(discretos, crudo);
      if (canonico === undefined) return { error: `«${crudo}» no es una opción de este campo.` };
      valores.push(canonico);
    } else {
      const normalizado = normalizarValorCondicion(tipo, crudo);
      if (normalizado === null) return { error: `«${crudo}» no es un valor válido para este campo.` };
      valores.push(normalizado);
    }
  }

  const unicos = [...new Set(valores)];
  if (unicos.length === 0) return { error: 'Elija al menos un valor' };
  return { condicion: { operador: condicion.operador, valores: unicos } };
}

/**
 * Aplana el árbol dejando solo lo que el ejecutor ve en este momento.
 *
 * Un ítem oculto oculta también a su descendencia: si la sección no se muestra,
 * sus campos tampoco, por más que ellos no tengan condición propia.
 */
export function itemsVisibles(items: ItemCongelado[], respuestas: MapaRespuestas): ItemCongelado[] {
  const salida: ItemCongelado[] = [];

  const recorrer = (lista: ItemCongelado[], padre: ItemCongelado | null): void => {
    for (const item of lista) {
      if (!cumpleCondicion(item, padre, respuestas)) continue;
      salida.push(item);
      recorrer(item.hijos, item);
    }
  };

  recorrer(items, null);
  return salida;
}

/** Árbol podado conservando la forma, para renderizar en la interfaz. */
export function arbolVisible(items: ItemCongelado[], respuestas: MapaRespuestas): ItemCongelado[] {
  const podar = (lista: ItemCongelado[], padre: ItemCongelado | null): ItemCongelado[] =>
    lista
      .filter((item) => cumpleCondicion(item, padre, respuestas))
      .map((item) => ({ ...item, hijos: podar(item.hijos, item) }));

  return podar(items, null);
}

/** Campos obligatorios visibles y aún sin responder: los que bloquean el cierre. */
export function requeridosPendientes(
  items: ItemCongelado[],
  respuestas: MapaRespuestas,
): ItemCongelado[] {
  // Mismo criterio que el ejecutor (`tieneRespuesta`): un texto vaciado o una
  // respuesta en blanco no cuentan, o el cierre de la OT dejaría pasar lo que
  // el checklist sigue exigiendo.
  return itemsVisibles(items, respuestas).filter(
    (i) => i.tipoItem === 'CAMPO' && i.requerido && !tieneRespuesta(respuestas, i.idCongelado),
  );
}

export function contarCampos(items: ItemCongelado[]): number {
  return items.reduce((total, i) => total + (i.tipoItem === 'CAMPO' ? 1 : 0) + contarCampos(i.hijos), 0);
}

/**
 * Cuenta aprobados/alertas/fallas/pendientes sobre los campos visibles de una
 * ejecución. Vive aquí (y no solo en `EjecucionService`) porque el cierre de
 * una OT de Inspección con procedimientos incompletos (§8.9, punto 30) necesita
 * el mismo cálculo para dejar un `resumen_resultados` coherente, sin duplicar
 * la lógica en dos servicios que deben coincidir.
 */
export function resumirResultados(visibles: ItemCongelado[], respuestas: MapaRespuestas): ResumenResultados {
  const campos = visibles.filter((i) => i.tipoItem === 'CAMPO');
  let aprobado = 0;
  let alerta = 0;
  let falla = 0;
  let sinResponder = 0;
  let requeridosPendientesN = 0;

  for (const campo of campos) {
    const respuesta = respuestas.get(campo.idCongelado);
    if (!respuesta) {
      sinResponder += 1;
      if (campo.requerido) requeridosPendientesN += 1;
      continue;
    }
    if (campo.tipoCampo !== 'INSPECCION') continue;
    if (respuesta.valor === 'APROBADO') aprobado += 1;
    else if (respuesta.valor === 'ALERTA') alerta += 1;
    else if (respuesta.valor === 'FALLA') falla += 1;
  }

  return { aprobado, alerta, falla, sinResponder, requeridosPendientes: requeridosPendientesN, totalVisibles: campos.length };
}

// ─── Historial de ejecuciones ────────────────────────────────────────────────

/**
 * Filtros del historial de ejecuciones (§8.10, punto 27 del backlog): mismo
 * patrón de fecha/activo/ubicación que ya usan OT, Solicitudes y Activos, para
 * que el historial de checklists no sea la única página sin poder acotar.
 */
export const filtroEjecucionSchema = paginacionSchema.extend({
  plantillaId: uuid.optional(),
  ubicacionId: uuid.optional(),
  activoId: uuid.optional(),
  estado: z.enum(['EN_PROGRESO', 'COMPLETADA']).optional(),
  fechaDesde: z.coerce.date().optional(),
  fechaHasta: z.coerce.date().optional(),
});
export type FiltroEjecucion = z.infer<typeof filtroEjecucionSchema>;

export interface EjecucionResumida {
  id: string;
  ordenTrabajoId: string;
  ordenTrabajoNumero: number;
  plantillaId: string;
  plantillaNombre: string;
  plantillaVersion: number;
  estado: 'EN_PROGRESO' | 'COMPLETADA';
  ubicacionId: string | null;
  ubicacion: string | null;
  activoId: string | null;
  activo: string | null;
  puntajeTotal: string | null;
  porcentaje: string | null;
  resumen: ResumenResultados;
  iniciadoEl: string;
  completadoEl: string | null;
}
